import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { ToastSeverity } from '../../../shared/enums/toast-severity.enum';
import {
  catchError,
  EMPTY,
  finalize,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastModule } from 'primeng/toast';
import { APP_PATHS } from '../../../core/config/app-paths';
import { ProgressSpinner } from 'primeng/progressspinner';
import { OrganizacjaApi } from '../../../shared/services/api/organizacja.api';
import { AuthApi } from '../../../shared/services/api/auth.api';
import { HttpErrorDetails } from '../../../core/services/http-error-response.service';

@Component({
  selector: 'app-join-organization',
  standalone: true,
  imports: [CommonModule, RouterModule, ToastModule, ProgressSpinner],
  templateUrl: './join-organization.component.html',
  styleUrls: ['./join-organization.component.scss'],
  providers: [DialogService],
})
export class JoinOrganizationComponent implements OnInit {
  #authApi = inject(AuthApi);
  #messageService = inject(MessageService);
  #destroyRef = inject(DestroyRef);
  #router = inject(Router);
  #organizationApi = inject(OrganizacjaApi);
  #route = inject(ActivatedRoute);

  #loading = signal(false);
  loading = this.#loading.asReadonly();
  #isLoggedIn = signal(false);

  ngOnInit(): void {
    this.#loading.set(true);

    this.#authApi
      .getCurrentUser$()
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.#isLoggedIn.set(false);
            return of(null);
          }
          return throwError(() => error);
        }),

        tap((user) => {
          if (user) this.#isLoggedIn.set(true);
        }),

        switchMap(() => {
          const token = this.#route.snapshot.queryParamMap.get('token');

          if (!token) {
            this.#messageService.add({
              severity: ToastSeverity.ERROR,
              summary: 'Niepoprawny token zaproszenia',
            });
            this.#navigateAfterInvalidToken();
            return EMPTY;
          }

          return this.#organizationApi.joinOrganizationByToken$(token);
        }),

        tap(() => {
          this.#messageService.add({
            severity: ToastSeverity.SUCCESS,
            summary: 'Pomyślnie dołączono do organizacji',
          });

          this.#router.navigate([
            APP_PATHS.auth.root,
            APP_PATHS.auth.applicationContext,
          ]);
        }),

        catchError((error) => this.#handleJoinOrganizationError(error)),
        finalize(() => this.#loading.set(false)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #navigateAfterInvalidToken(): void {
    if (this.#isLoggedIn()) {
      this.#router.navigate([
        APP_PATHS.auth.root,
        APP_PATHS.auth.applicationContext,
      ]);
    } else {
      this.#router.navigate([
        APP_PATHS.auth.root,
        APP_PATHS.auth.applicationContext,
      ]);
    }
  }

  #handleJoinOrganizationError(error: HttpErrorResponse): Observable<null> {
    const errorData = error.error as HttpErrorDetails;
    const detail = errorData?.message;

    this.#isLoggedIn.set(false);

    this.#messageService.add({
      severity: ToastSeverity.INFO,
      summary: detail || 'Sesja wygasła. Zaloguj się ponownie.',
      life: 8500,
    });

    this.#router.navigate([
      APP_PATHS.auth.root,
      APP_PATHS.auth.applicationContext,
    ]);
    return of(null);

    this.#router.navigate(['/']);
    return of(null);
  }
}
